import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  systemTheme: "light" | "dark";
  effectiveTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  forcedTheme?: "light" | "dark";
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, forcedTheme }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "system";
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
      }
    } catch {
      // Storage blocked (site data disabled) is an expected browser setting, not an error: fall back to "system"
      // silently. Logging it put two console.error breadcrumbs ahead of every Sentry event from such browsers.
    }

    return "system";
  });

  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "light";

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  const effectiveTheme = forcedTheme || (theme === "system" ? systemTheme : theme);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      if (forcedTheme) {
        console.warn("Cannot change theme when forcedTheme is set");

        return;
      }
      setThemeState(newTheme);
      try {
        localStorage.setItem("theme", newTheme);
      } catch {
        // Not persisted (storage blocked or full); the theme still applies for this page.
      }
    },
    [forcedTheme]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    handleChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);

      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      const listener = (e: MediaQueryListEvent) => handleChange(e);
      mediaQuery.addListener(listener);

      return () => mediaQuery.removeListener(listener);
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme: forcedTheme ? (forcedTheme as Theme) : theme,
        setTheme,
        systemTheme,
        effectiveTheme: effectiveTheme as "light" | "dark",
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
